import { useCallback, useEffect, useMemo, useState } from 'react'

import axios from 'axios'

import { useSendCandidateMessageBulkAction } from '../contexts/CandidateService'
import { useJHireURL } from '../contexts/Domain'

interface CandidateMessageTemplate {
    EId: string
    body: string
    channel: string
    class: string
    content: string
    default: boolean
    description: string
    flags: number
    id: number
    isDefault: boolean
    languageTag: null
    locale: string
    message: FunctionStringCallback
    multipleTemplateBody: null
    multipleTemplateSubject: null
    section: null
    subject: string
    title: string
}

export type CandidateMessageTemplateWithLocale = CandidateMessageTemplate & {
    group: string
}

export interface SendMessageTemplateResponse {
    templates: {
        [key: string]: Array<CandidateMessageTemplate>
    }
}

interface GetCandidateMessageTemplatesProps {
    applications: Array<string>
}
export function useGetCandidateMessageTemplates(props: GetCandidateMessageTemplatesProps) {
    const jhireURL = useJHireURL()

    const [loading, setLoading] = useState(true)
    const [data, setData] = useState<SendMessageTemplateResponse>()
    const [templates, setTemplates] = useState<Array<CandidateMessageTemplateWithLocale>>([])
    const [error, setError] = useState('')

    useEffect(() => {
        if (props.applications && props.applications.length > 0) {
            setLoading(true)
            setError('')

            let params = ''
            for(let i = 0; i < props.applications.length; i++) {
                const application = props.applications[i]
                params += `applicationEIds%5B%5D=${application}&`
            }

            axios.get<SendMessageTemplateResponse>(`${jhireURL}/action/application/template/SendMessageToCandidate?${params}`)
                .then((response) => {
                    setData(response.data)
                    const templates: Array<CandidateMessageTemplateWithLocale> = []
                    Object.keys(response.data.templates)
                        .forEach(key => response.data.templates[key]
                        .forEach(template => templates.push({ ...template, group: key })))

                    templates.sort((a, b) => -b.group.localeCompare(a.group))
                    setTemplates(templates)
                })
                .catch((err) => {
                    setError('An unknown error occurred')
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [props, props.applications])

    return useMemo(() => ({
        loading,
        error,
        data,
        templates,
    }), [
        loading,
        error,
        data,
        templates
    ])
}

export interface SendCandidateBulkMessagePayload {
    attachmentList: Array<string>
    content: string
    entityIdList: Array<string>
    locale: string
    sendAnonymously: boolean
    subject: string
}

export interface SendCandidateBulkMessageResponse {
    msgInfo: number
}

export function useSendCandidateMessageReq() {
    const sendMessage = useSendCandidateMessageBulkAction()

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [response, setResponse] = useState<SendCandidateBulkMessageResponse>(null)

    const sendMessageRequest = useCallback((data: SendCandidateBulkMessagePayload) => {
        setLoading(true)
        setError('')
        return new Promise(function(resolve, reject) {
            try {
                sendMessage(data, function(response) {
                    setLoading(false)
                    if (response.status === 200 && response.data.msgInfo === 200) {
                        setResponse(response.data)
                        resolve(response.data)
                    } else {
                        setError('An error occurred while trying to send message')
                        reject(response.data)
                    }
                })
            } catch(err) {
                reject(err)
            }
        })
    }, [sendMessage])

    return useMemo(() => ({
        loading,
        error,
        response,
        sendMessageRequest,
    }), [
        loading,
        error,
        response,
        sendMessageRequest,
    ])
}
